import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} SetQuizLimitData
 * @property {number|string} classroom_id - The classroom ID
 * @property {number|string} limit - The quiz limit to set
 */

/**
 * @description Custom hook to set quiz limit for a classroom
 */
export const useSetQuizLimitClassroom = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * Set quiz limit for a classroom
     * @async
     * @param {SetQuizLimitData} data - The quiz limit data
     * @param {(success:boolean) => void} [onSuccess] - Callback on success
     * @returns {Promise<any|null>} The response data or null on failure
     */
    const setQuizLimitClassroom = async (data, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            const url = `${TENANT_DOMAIN}/quiz/limit/classroom`;
            const response = await axiosInstance.post(url, {
                classroom_id: data.classroom_id,
                limit: data.limit,
            });

            const responseData = response?.data?.data || null;

            setSuccess(true);
            if (onSuccess) onSuccess(true);
            return responseData;
        } catch (err) {
            setSuccess(false);
            if (onSuccess) onSuccess(false);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

                setError(
                    firstError ||
                    err?.response?.data?.message ||
                    ERROR_MESSAGES.UNKNOWN,
                );
            }
            return null;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        error,
        success,
        setQuizLimitClassroom,
    };
};
